import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { currentSpaPath } from '@/shared/api';
import { publishGlobalFeedback } from '@/shared/feedback';
import type { GlobalFeedbackSeverity } from '@/shared/feedback';
import { useSession } from '@/shared/session';

import { resolvePasskeyEnrolmentOutcome, takePasskeyEnrolmentOutcome } from './passkeyEnrolment';
import type { PasskeyEnrolmentOutcome } from './passkeyEnrolment';

const OUTCOME_SEVERITY: Record<PasskeyEnrolmentOutcome, GlobalFeedbackSeverity> = {
  success: 'success',
  cancelled: 'info',
  error: 'error',
  unknown: 'info',
};

const OUTCOME_MESSAGE_KEY: Record<PasskeyEnrolmentOutcome, string> = {
  success: 'passkeyEnrolment.success',
  cancelled: 'passkeyEnrolment.cancelled',
  error: 'passkeyEnrolment.error',
  unknown: 'passkeyEnrolment.unknown',
};

/**
 * The "elsewhere" half of the passkey enrolment round trip (design doc §11 step 4;
 * user-story-merchant-portal-060 AC8, adr-merchant-portal-020 D4).
 *
 * `/account/security` consumes its own outcome inline (it is already on screen to show the alert).
 * Everywhere else — the nudge was answered on some other page — this render-nothing component reports
 * the same outcome to the global feedback snackbar instead, so the user is not left wondering whether
 * anything happened.
 *
 * Mounted once in `AppLayout`, **after** `StepUpReplay` — both publish onto the global feedback bus from
 * their own mount effect, and that bus keeps exactly one listener (`GlobalFeedbackSnackbar`'s), so
 * mounting after it (and after `StepUpReplay`) is what guarantees a listener is already registered.
 *
 * Skips consumption while the current path is under `/account/` — not just `/account/security` itself,
 * so a future sibling page under the same reserved prefix (design doc §5) does not accidentally race the
 * one this component is deferring to.
 */
export function PasskeyEnrolmentReturn() {
  const { t } = useTranslation();
  const location = useLocation();
  const session = useSession();
  // A document-lifetime guard, exactly like StepUpReplay's `replayStarted` — survives StrictMode's
  // double-invoked effects so the snackbar cannot fire twice for one outcome.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) {
      return;
    }
    if (location.pathname === '/account' || location.pathname.startsWith('/account/')) {
      return;
    }
    const pending = takePasskeyEnrolmentOutcome(currentSpaPath());
    if (!pending) {
      return;
    }
    consumed.current = true;

    const outcome = resolvePasskeyEnrolmentOutcome(pending, session.passkeyEnrolled);
    publishGlobalFeedback({
      severity: OUTCOME_SEVERITY[outcome],
      message: t(OUTCOME_MESSAGE_KEY[outcome]),
    });
  }, [location.pathname, session.passkeyEnrolled, t]);

  return null;
}
